import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button
} from '@mui/material';
import {
  Lock as LockIcon,
  Home as HomeIcon
} from '@mui/icons-material';

const PermissionRoute = ({
  children,
  requiredPermission,
  requiredPermissions = [],
  requireAny = false,
  fallbackTo = '/'
}) => {
  const { isAuthenticated, hasPermission, hasAnyPermission } = useAuth();

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check permissions
  let hasAccess = false;

  if (requiredPermission) {
    hasAccess = hasPermission(requiredPermission);
  } else if (requiredPermissions.length > 0) {
    if (requireAny) {
      hasAccess = hasAnyPermission(requiredPermissions);
    } else {
      hasAccess = requiredPermissions.every(permission => hasPermission(permission));
    }
  } else {
    // No specific permissions required, just authentication
    hasAccess = true;
  }

  // If user has access, render the children
  if (hasAccess) {
    return children;
  }

  // If no access, show permission denied page
  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        <LockIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        <Typography variant="h4" component="h1" gutterBottom color="error">
          Access Denied
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          You don't have permission to access this page
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {requiredPermission && `Required permission: ${getPermissionDisplayName(requiredPermission)}`}
          {requiredPermissions.length > 0 &&
            `Required permissions: ${requiredPermissions.map(getPermissionDisplayName).join(requireAny ? ' or ' : ' and ')}`
          }
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={() => window.location.href = fallbackTo}
          >
            Go to Dashboard
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

const getPermissionDisplayName = (permission) => {
  const displayNames = {
    'ccdRegistrations': 'CCD Registrations',
    'parishRegistrations': 'Parish Registrations',
    'staffManagement': 'Staff Management',
    'massManagement': 'Mass Management',
    'ociaRegistrations': 'OCIA Registrations',
    'campusMinistryRegistrations': 'Campus Ministry'
  };
  return displayNames[permission] || permission;
};

export default PermissionRoute;